import { AxiosError } from 'axios'
import { QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { handleServerError } from '@/shared/lib/handle-server-error'
import { reportUnexpectedClientFailure } from '@/shared/lib/reportUnexpectedClientFailure'

type LexQueryCacheAuthConfig = {
  kind: 'lex'
  sessionQueryKey: string
  signInPath: string
  authPaths: string[]
}

type PpwrQueryCacheAuthConfig = {
  kind: 'ppwr-supplier-portal'
}

type CatalogSupplierPortalQueryCacheAuthConfig = {
  kind: 'catalog-supplier-portal'
}

type QueryCacheAuthConfig =
  | LexQueryCacheAuthConfig
  | PpwrQueryCacheAuthConfig
  | CatalogSupplierPortalQueryCacheAuthConfig

type QueryClientOptions = NonNullable<
  ConstructorParameters<typeof QueryClient>[0]
>

type CreateQueryClientWithFailureReportingArgs = {
  auth: QueryCacheAuthConfig
  queryDefaults?: NonNullable<QueryClientOptions['defaultOptions']>['queries']
  enableMutationToasts?: boolean
}

function isInitialUserVisibleQueryFailure(query: {
  state: { data: unknown }
}): boolean {
  return query.state.data === undefined
}

function reportQueryFailureIfNeeded(
  error: unknown,
  query: { state: { data: unknown } }
): void {
  if (!isInitialUserVisibleQueryFailure(query)) {
    return
  }

  reportUnexpectedClientFailure({
    error,
    severity: 'failed_action',
  })
}

function reportMutationFailureIfNeeded(error: unknown): void {
  reportUnexpectedClientFailure({
    error,
    severity: 'failed_action',
  })
}

function handleLexQueryCacheAuthError(
  error: unknown,
  queryClient: QueryClient,
  config: LexQueryCacheAuthConfig
): void {
  if (!(error instanceof AxiosError)) {
    return
  }

  if (error.response?.status === 401) {
    toast.error('Session expired!')
    queryClient.removeQueries({ queryKey: [config.sessionQueryKey] })
    const pathname = window.location.pathname
    const isAuthPath = config.authPaths.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`)
    )
    if (!isAuthPath) {
      const redirect = window.location.href
      window.location.assign(
        `${config.signInPath}?redirect=${encodeURIComponent(redirect)}`
      )
    } else {
      window.location.assign(config.signInPath)
    }
    return
  }

  if (error.response?.status === 500) {
    toast.error('Internal Server Error!')
    if (import.meta.env.PROD) {
      window.location.assign('/500')
    }
  }
}

export function createQueryClientWithFailureReporting(
  args: CreateQueryClientWithFailureReportingArgs
): QueryClient {
  const enableMutationToasts = args.enableMutationToasts ?? true

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: args.queryDefaults,
      mutations: {
        onError: (error) => {
          if (error instanceof AxiosError && error.response?.status === 409) {
            return
          }

          reportMutationFailureIfNeeded(error)

          if (!enableMutationToasts) {
            return
          }

          handleServerError(error)

          if (error instanceof AxiosError && error.response?.status === 304) {
            toast.error('Content not modified!')
          }
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        reportQueryFailureIfNeeded(error, query)

        if (args.auth.kind === 'lex') {
          handleLexQueryCacheAuthError(error, queryClient, args.auth)
        }
      },
    }),
  })

  return queryClient
}
